import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ForkliftIcon from "@mui/icons-material/Agriculture";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import { fetchInspection } from "../../views/Fleet/fleetApi";
import { Spinner } from "../UI/States";
import {
  HISTORY_DAYS,
  RESULT_META,
  RESULT_RANK,
  SERVICE_REASONS,
  SOURCE_LABEL,
  TONE_CHIP,
  isQuick,
  formatDate,
  formatDateTime,
  formatDuration,
  formatMiles,
  localDayKey,
  startOfLocalDay,
} from "./fleetFormat";

/* ------------------------------------------------------------------
   Building blocks shared by the Fleet pages (/fleet…), the homepage
   Fleet widget, and the branch kiosk's check-in history.
------------------------------------------------------------------ */

export const Pill = ({ tone = "slate", icon: Icon, children, className = "" }) => (
  <span
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${
      TONE_CHIP[tone] || TONE_CHIP.slate
    } ${className}`}
  >
    {Icon && <Icon sx={{ fontSize: 14 }} />}
    {children}
  </span>
);

export const ResultChip = ({ result }) => {
  const meta = RESULT_META[result];
  if (!meta) return <span className="app-muted">—</span>;
  return <Pill tone={meta.tone}>{meta.label}</Pill>;
};

export const ServiceChip = ({ inService, service }) =>
  inService ? (
    <Pill tone="emerald">In service</Pill>
  ) : (
    <span title={service?.reasonLabel ? `${service.reasonLabel}${service.note ? ` — ${service.note}` : ""}` : undefined}>
      <Pill tone="rose">Out of service</Pill>
    </span>
  );

/** "Waiting on parts · since Oct 3 · back Oct 10" for list rows. */
export const serviceSummary = (service) =>
  service
    ? [
        service.reasonLabel,
        service.since ? `since ${formatDate(service.since)}` : "",
        service.expectedBack ? `back ${formatDate(`${service.expectedBack}T12:00:00`)}` : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

/** Banner on a unit's page while it's out of service. */
export const ServiceNotice = ({ service }) =>
  service ? (
    <div role="status" className={`mb-6 rounded-2xl px-4 py-3 text-sm ${TONE_CHIP.violet}`}>
      <p className="font-semibold">Out of service — {service.reasonLabel}</p>
      {service.note && <p className="mt-0.5">{service.note}</p>}
      <p className="mt-1 text-xs opacity-90">
        {[
          service.since ? `Since ${formatDateTime(service.since)}` : "",
          service.by ? `by ${service.by}` : "",
          service.source === "not-safe" ? "(Not Safe report)" : service.source === "kiosk" ? "(branch kiosk)" : "",
          service.expectedBack ? `· Expected back ${formatDate(`${service.expectedBack}T12:00:00`)}` : "",
        ]
          .filter(Boolean)
          .join(" ")}
      </p>
    </div>
  ) : null;

/**
 * Take a unit out of service (a reason is required; "Other" needs details)
 * or put it back. onSubmit({ inService, reason, note, expectedBack }) should
 * throw with a message on failure.
 */
export const ServiceDialog = ({ open, assetName, inService, onClose, onSubmit }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [expectedBack, setExpectedBack] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const goingOut = inService;

  useEffect(() => {
    if (open) {
      setReason("");
      setNote("");
      setExpectedBack("");
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    if (busy) return;
    if (goingOut && !reason) return setError("Pick a reason.");
    if (goingOut && reason === "other" && !note.trim()) return setError("Describe the reason when you pick Other.");
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        inService: !goingOut,
        ...(goingOut ? { reason, expectedBack } : {}),
        note: note.trim(),
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { m: 2 } }} aria-labelledby="fleet-service-title">
      <div className="p-5 sm:p-6">
        <h2 id="fleet-service-title" className="text-lg font-semibold">
          {goingOut ? `Mark ${assetName} out of service` : `Put ${assetName} back in service`}
        </h2>
        <p className="app-muted mt-1 text-sm">
          {goingOut
            ? "Out of service is for a unit that can't be used — it's logged with the reason in the unit's history."
            : "This is logged in the unit's history."}
        </p>

        {goingOut && (
          <div className="mt-5">
            <p className="app-eyebrow mb-2">Reason</p>
            <div role="radiogroup" aria-label="Reason" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {SERVICE_REASONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={reason === option.value}
                  onClick={() => setReason(option.value)}
                  className={`rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition ${
                    reason === option.value
                      ? "border-violet-500 bg-violet-500/15 text-violet-800 dark:text-violet-200"
                      : "app-surface hover:border-violet-400"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 space-y-4">
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label={goingOut ? (reason === "other" ? "Details (required)" : "Details") : "What was done? (optional)"}
            placeholder={goingOut ? "e.g. brake job scheduled, hydraulic pump on order" : "e.g. new brake pads, passed inspection"}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
          {goingOut && (
            <TextField
              type="date"
              size="small"
              label="Expected back (optional)"
              value={expectedBack}
              onChange={(e) => setExpectedBack(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: localDayKey(new Date()) }}
              className="w-full sm:w-56"
            />
          )}
        </div>

        {error && (
          <p role="alert" className="mt-3 rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className="app-btn app-btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={`app-btn ${goingOut ? "app-btn-danger" : "app-btn-primary"}`}
            onClick={submit}
            disabled={busy}
          >
            {busy ? "Saving…" : goingOut ? "Mark out of service" : "Mark in service"}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export const AssetIcon = ({ type, className = "" }) => {
  const Icon = type === "vehicle" ? LocalShippingRoundedIcon : ForkliftIcon;
  return (
    <span
      className={`app-icon-tile h-9 w-9 rounded-xl bg-teal-500/15 text-teal-600 dark:text-teal-300 ${className}`}
    >
      <Icon fontSize="small" />
    </span>
  );
};

export const BackLink = ({ to, children }) => (
  <Link
    to={to}
    className="app-muted mb-3 inline-flex min-h-[2.5rem] items-center gap-1 text-sm font-medium hover:text-[color:var(--app-accent)]"
  >
    <ArrowBackRoundedIcon fontSize="small" /> {children}
  </Link>
);

export const Notice = ({ tone = "amber", children }) => (
  <p
    role="status"
    className={`mb-4 flex items-start gap-2 rounded-xl px-3 py-2 text-sm ${
      TONE_CHIP[tone] || TONE_CHIP.amber
    }`}
  >
    <ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} className="mt-px shrink-0" />
    <span>{children}</span>
  </p>
);

// Segmented control — the same look as the PCW dashboard's / MUI
// ToggleButtonGroup in the app theme.
export const Segmented = ({ options, value, onChange, label }) => (
  <div
    role="group"
    aria-label={label}
    className="flex w-fit max-w-full gap-0.5 overflow-x-auto rounded-xl bg-[color:var(--app-hover)] p-[3px]"
  >
    {options.map((option) => {
      const active = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(option.value)}
          className={`inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[9px] px-3 py-[5px] text-xs font-semibold transition ${
            active
              ? "bg-white text-[color:var(--app-text)] shadow-sm dark:bg-white/15"
              : "app-muted hover:text-[color:var(--app-text)]"
          }`}
        >
          {option.label}
          {option.count !== undefined && (
            <span className="tabular-nums opacity-70">{option.count}</span>
          )}
        </button>
      );
    })}
  </div>
);

export const SearchField = ({ value, onChange, placeholder, className = "w-full sm:w-72" }) => (
  <TextField
    size="small"
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={className}
    inputProps={{ "aria-label": placeholder }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchRoundedIcon fontSize="small" />
        </InputAdornment>
      ),
    }}
  />
);

export const BranchSelect = ({ value, onChange, branches, label = "Branch", className = "w-full sm:w-56" }) => (
  <FormControl size="small" className={className}>
    <InputLabel id="fleet-branch-filter">{label}</InputLabel>
    <Select
      labelId="fleet-branch-filter"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <MenuItem value="">All branches</MenuItem>
      {branches.map((branch) => (
        <MenuItem key={branch} value={branch}>
          {branch}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

/** Label / value pair for detail cards. */
export const Fact = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="app-eyebrow">{label}</dt>
    <dd className="mt-0.5 break-words text-sm font-medium">
      {children === null || children === undefined || children === "" ? "—" : children}
    </dd>
  </div>
);

// ------------------------------------------------------ activity calendar

// Solid = someone inspected; tinted = a status with no inspection; red
// outline = drove with no inspection. Literal strings so Tailwind keeps them.
const CALENDAR_TONES = {
  pass: "bg-emerald-600 text-white",
  attention: "bg-amber-400 text-amber-950",
  unsafe: "bg-rose-600 text-white",
  missed: "bg-rose-500/10 text-rose-700 ring-2 ring-inset ring-rose-500 dark:text-rose-300",
  oos: "bg-violet-500/25 text-violet-800 dark:text-violet-200",
  out: "bg-slate-500/25 text-slate-700 dark:text-slate-200",
  none: "bg-[color:var(--app-hover)] text-[color:var(--app-text-muted)] ring-1 ring-inset ring-[color:var(--app-divider)]",
};

// Legend order: inspections first, then the no-inspection states.
const CALENDAR_STATES = ["pass", "attention", "unsafe", "missed", "oos", "out", "none"];
const stateLabel = (state) => (state === "none" ? "Nothing recorded" : RESULT_META[state].label);

// Results that mean someone actually inspected (not GPS or status rows).
const INSPECTED_RESULTS = new Set(["pass", "attention", "unsafe"]);

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const TODAY_OUTLINE = "outline outline-2 outline-offset-1 outline-[color:var(--app-text)]";

/**
 * Month calendars (newest first) covering the last `days` days: each day is
 * colored by its worst result, with the date printed inside. Clicking a day
 * that has entries calls onSelectDay(dayKey) (click again to clear).
 * `noun` names one entry in tooltips ("inspection" / "check-in").
 */
export const ActivityCalendar = ({ entries, days = HISTORY_DAYS, selectedDay, onSelectDay, noun = "inspection" }) => {
  const { months, counts, todayKey } = useMemo(() => {
    const byDay = new Map();
    entries.forEach((entry) => {
      const key = localDayKey(entry.at);
      // "Back in service" marks a moment, not how the day went.
      if (!key || entry.result === "back") return;
      const day = byDay.get(key) || { inspections: 0, worst: "pass" };
      if (INSPECTED_RESULTS.has(entry.result)) day.inspections += 1;
      if ((RESULT_RANK[entry.result] ?? 0) > (RESULT_RANK[day.worst] ?? 0)) {
        day.worst = entry.result;
      }
      byDay.set(key, day);
    });

    const first = startOfLocalDay(days - 1);
    const today = startOfLocalDay(0);
    const tally = {};

    const monthStarts = [];
    for (
      let month = new Date(first.getFullYear(), first.getMonth(), 1);
      month <= today;
      month = new Date(month.getFullYear(), month.getMonth() + 1, 1)
    ) {
      monthStarts.push(month);
    }

    const built = monthStarts.map((start) => {
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      const from = start < first ? first : start;
      const to = end > today ? today : end;
      // Only the weeks that hold an in-range day, Sunday first.
      const cursor = new Date(from);
      cursor.setDate(cursor.getDate() - cursor.getDay());
      const cells = [];
      while (cursor <= to) {
        for (let i = 0; i < 7; i += 1) {
          const date = new Date(cursor);
          const key = localDayKey(date);
          const inMonth = date.getMonth() === start.getMonth();
          const inRange = inMonth && date >= first && date <= today;
          const day = inRange ? byDay.get(key) || null : null;
          const state = inRange ? day?.worst || "none" : null;
          if (state) tally[state] = (tally[state] || 0) + 1;
          cells.push({ key, date, inMonth, inRange, day, state });
          cursor.setDate(cursor.getDate() + 1);
        }
      }
      return {
        key: localDayKey(start),
        label: start.toLocaleDateString([], {
          month: "long",
          ...(start.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
        }),
        cells,
      };
    });

    return { months: built.reverse(), counts: tally, todayKey: localDayKey(today) };
  }, [entries, days]);

  const describe = (cell) => {
    const date = cell.date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
    const n = cell.day?.inspections || 0;
    return `${date} — ${stateLabel(cell.state)}${n ? ` · ${n} ${noun}${n === 1 ? "" : "s"}` : ""}`;
  };

  return (
    <div>
      <div className="flex flex-wrap gap-x-8 gap-y-5">
        {months.map((month) => (
          <div key={month.key}>
            <p className="mb-1.5 text-sm font-semibold">{month.label}</p>
            <div className="grid grid-cols-[repeat(7,1.75rem)] gap-1">
              {WEEKDAYS.map((weekday, i) => (
                <span key={i} aria-hidden="true" className="app-muted text-center text-[10px] font-semibold">
                  {weekday}
                </span>
              ))}
              {month.cells.map((cell) => {
                if (!cell.inMonth) return <span key={cell.key} className="h-7 w-7" />;
                // Same month but before the window or still to come.
                if (!cell.inRange) {
                  return (
                    <span
                      key={cell.key}
                      aria-hidden="true"
                      className="app-muted flex h-7 w-7 items-center justify-center text-[11px] tabular-nums opacity-40"
                    >
                      {cell.date.getDate()}
                    </span>
                  );
                }
                const label = describe(cell);
                const selected = selectedDay === cell.key;
                const square = `flex h-7 w-7 items-center justify-center rounded-md text-[11px] font-semibold tabular-nums ${
                  CALENDAR_TONES[cell.state]
                } ${
                  selected
                    ? "outline outline-2 outline-offset-2 outline-sky-500"
                    : cell.key === todayKey
                    ? TODAY_OUTLINE
                    : ""
                }`;
                // Only days with entries are clickable; the rest are plain
                // squares so their tooltip still shows (disabled buttons
                // swallow hover).
                if (!onSelectDay || !cell.day) {
                  return (
                    <span key={cell.key} title={label} aria-label={label} role="img" className={square}>
                      {cell.date.getDate()}
                    </span>
                  );
                }
                return (
                  <button
                    key={cell.key}
                    type="button"
                    title={label}
                    aria-label={label}
                    aria-pressed={selected}
                    onClick={() => onSelectDay(selected ? null : cell.key)}
                    className={`${square} cursor-pointer transition hover:opacity-80`}
                  >
                    {cell.date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="app-muted mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
        <span>Of the last {days} days:</span>
        {CALENDAR_STATES.filter((state) => counts[state]).map((state) => (
          <span key={state} className="inline-flex items-center gap-1.5">
            <span className={`h-3 w-3 rounded-[3px] ${CALENDAR_TONES[state]}`} />
            {stateLabel(state)}
            <strong className="tabular-nums text-[color:var(--app-text)]">{counts[state]}</strong>
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className={`h-3 w-3 rounded-[3px] ${TODAY_OUTLINE}`} />
          Today
        </span>
      </div>
    </div>
  );
};

// ------------------------------------------------------ inspection table

const assetPath = (entry) => {
  if (entry.type === "vehicle" && entry.vehicleId) return `/fleet/vehicles/${entry.vehicleId}`;
  if (entry.type === "forklift" && entry.forkliftId) return `/fleet/forklifts/${entry.forkliftId}`;
  return null;
};

export const assetName = (entry) =>
  entry.type === "vehicle" ? `Vehicle ${entry.asset}` : `Unit #${entry.asset}`;

/**
 * Paged inspection list. Rows open the detail dialog via onOpen(entry).
 * show: { asset, branch, odometer, source } toggles optional columns.
 */
export const InspectionTable = ({
  entries,
  onOpen,
  show = {},
  pageSize = 25,
  emptyTitle = "No inspections found.",
}) => {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(entries.length / pageSize));
  // Branches that run shifts (SBC) get a Shift column automatically.
  const showShift = useMemo(() => entries.some((e) => e.shiftName), [entries]);
  // Inspections the app timed get a Time column (quick ones highlighted).
  const showTime = useMemo(
    () => entries.some((e) => e.durationSeconds !== null && e.durationSeconds !== undefined),
    [entries]
  );

  useEffect(() => {
    setPage(0);
  }, [entries]);

  if (!entries.length) {
    return <p className="app-muted px-4 py-10 text-center text-sm">{emptyTitle}</p>;
  }

  const rows = entries.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <>
      <div className="overflow-x-auto">
        <table className="app-table">
          <thead>
            <tr>
              <th className="!px-4">When</th>
              {show.asset && <th>Unit</th>}
              {show.branch && <th>Branch</th>}
              {showShift && <th>Shift</th>}
              <th>Inspector</th>
              <th>Result</th>
              <th>Flagged items</th>
              {show.odometer && <th>Odometer</th>}
              {showTime && <th>Time</th>}
              {show.source && <th>Source</th>}
              <th aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => {
              const path = show.asset ? assetPath(entry) : null;
              return (
                <tr
                  key={entry.key}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${assetName(entry)} inspection from ${formatDateTime(entry.at)}`}
                  onClick={() => onOpen(entry)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpen(entry);
                    }
                  }}
                  className="cursor-pointer focus:outline-none focus-visible:bg-[color:var(--app-hover)]"
                >
                  <td className="!px-4 whitespace-nowrap tabular-nums">
                    {entry.source === "gps" ? formatDate(entry.at) : formatDateTime(entry.at)}
                  </td>
                  {show.asset && (
                    <td className="whitespace-nowrap">
                      <span className="inline-flex items-center gap-2">
                        {entry.type === "vehicle" ? (
                          <LocalShippingRoundedIcon sx={{ fontSize: 16 }} className="app-muted" />
                        ) : (
                          <ForkliftIcon sx={{ fontSize: 16 }} className="app-muted" />
                        )}
                        {path ? (
                          <Link
                            to={path}
                            onClick={(e) => e.stopPropagation()}
                            className="app-link font-semibold tabular-nums"
                          >
                            {entry.type === "vehicle" ? entry.asset : `#${entry.asset}`}
                          </Link>
                        ) : (
                          <span className="font-semibold tabular-nums">
                            {entry.type === "vehicle" ? entry.asset : `#${entry.asset}`}
                          </span>
                        )}
                      </span>
                    </td>
                  )}
                  {show.branch && <td className="whitespace-nowrap">{entry.branch || "—"}</td>}
                  {showShift && <td className="whitespace-nowrap">{entry.shiftName || <span className="app-muted">—</span>}</td>}
                  <td className="whitespace-nowrap">{entry.inspector || "—"}</td>
                  <td>
                    <ResultChip result={entry.result} />
                  </td>
                  <td className="max-w-[18rem]">
                    {entry.flagged?.length ? (
                      <span className="line-clamp-2 text-sm">
                        {entry.flagged.map((f) => f.label).join(", ")}
                      </span>
                    ) : (
                      <span className="app-muted">—</span>
                    )}
                  </td>
                  {show.odometer && (
                    <td className="whitespace-nowrap tabular-nums">{formatMiles(entry.odometer)}</td>
                  )}
                  {showTime && (
                    <td className="whitespace-nowrap tabular-nums">
                      {entry.durationSeconds === null || entry.durationSeconds === undefined ? (
                        <span className="app-muted">—</span>
                      ) : isQuick(entry) ? (
                        <span title="Under 30 seconds for the whole checklist">
                          <Pill tone="amber">{formatDuration(entry.durationSeconds)}</Pill>
                        </span>
                      ) : (
                        formatDuration(entry.durationSeconds)
                      )}
                    </td>
                  )}
                  {show.source && (
                    <td className="app-muted whitespace-nowrap text-xs">{SOURCE_LABEL[entry.source] || "—"}</td>
                  )}
                  <td className="w-8 text-right">
                    <ChevronRightRoundedIcon className="app-muted" fontSize="small" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t app-divider px-4 py-3">
          <span className="app-muted text-sm tabular-nums">
            {page * pageSize + 1}–{Math.min(entries.length, (page + 1) * pageSize)} of{" "}
            {entries.length.toLocaleString()}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              className="app-btn app-btn-sm app-btn-ghost"
              onClick={() => setPage(0)}
              disabled={page === 0}
            >
              First
            </button>
            <button
              type="button"
              className="app-btn app-btn-sm app-btn-secondary"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Prev
            </button>
            <button
              type="button"
              className="app-btn app-btn-sm app-btn-secondary"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={page >= pageCount - 1}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ----------------------------------------------------- inspection dialog

const STATUS_ICON = {
  ok: <CheckCircleRoundedIcon sx={{ fontSize: 18 }} className="text-emerald-600 dark:text-emerald-400" />,
  attention: <WarningAmberRoundedIcon sx={{ fontSize: 18 }} className="text-amber-600 dark:text-amber-400" />,
  na: <RemoveCircleOutlineRoundedIcon sx={{ fontSize: 18 }} className="app-muted" />,
};

/**
 * Full inspection: result, flagged items with notes, every checklist answer.
 * Pass a summary from the log and it fetches the checklist (needs userId);
 * pass an entry that already has `checklist` and it renders straight away
 * (the public kiosk history does this).
 */
export const InspectionDialog = ({ entry, onClose, userId, showAssetLink = false }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    if (!entry) {
      setDetail(null);
      return undefined;
    }
    if (entry.checklist) {
      setDetail(entry);
      return undefined;
    }
    let active = true;
    setDetail(null);
    fetchInspection(userId, entry)
      .then((full) => active && setDetail({ ...entry, ...full }))
      .catch((e) => active && setError(e.message));
    return () => {
      active = false;
    };
  }, [entry, userId]);

  const shown = detail || entry;
  const meta = shown ? RESULT_META[shown.result] : null;
  const path = shown && showAssetLink ? assetPath(shown) : null;

  return (
    <Dialog
      open={Boolean(entry)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { m: 2 } }}
      aria-labelledby="fleet-inspection-title"
    >
      {shown && (
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <AssetIcon type={shown.type} />
            <div className="min-w-0 flex-1">
              <h2 id="fleet-inspection-title" className="text-lg font-semibold leading-tight">
                {assetName(shown)}{" "}
                {shown.source === "status"
                  ? shown.result === "oos"
                    ? "— out of service"
                    : "— back in service"
                  : shown.source === "gps"
                    ? "— no inspection"
                    : "inspection"}
              </h2>
              <p className="app-muted text-sm tabular-nums">
                {shown.source === "gps" ? formatDate(shown.at) : formatDateTime(shown.at)}
                {shown.branch ? ` · ${shown.branch}` : ""}
                {shown.shiftName ? ` · ${shown.shiftName}` : ""}
              </p>
            </div>
          </div>

          <div
            className={`mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-3 py-2.5 text-sm ${
              TONE_CHIP[meta?.tone] || TONE_CHIP.slate
            }`}
          >
            <strong>{meta?.label || "—"}</strong>
            <span>by {shown.inspector || "unknown"}</span>
            <span className="opacity-80">via {SOURCE_LABEL[shown.source] || "—"}</span>
            {shown.entryID && <span className="opacity-80 tabular-nums">Entry #{shown.entryID}</span>}
          </div>

          {shown.flagged?.length > 0 && (
            <div className="mt-4">
              <p className="app-eyebrow mb-1.5">{shown.source === "status" || shown.result === "oos" ? "Reason" : "Flagged items"}</p>
              <ul className="space-y-1.5">
                {shown.flagged.map((item) => (
                  <li key={item.label} className="app-surface rounded-xl border px-3 py-2 text-sm">
                    <span className="font-semibold">{item.label}</span>
                    {item.note && <span className="app-muted block">{item.note}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {shown.notes && (
            <div className="mt-4">
              <p className="app-eyebrow mb-1.5">Notes</p>
              <p className="app-surface whitespace-pre-wrap rounded-xl border px-3 py-2 text-sm">{shown.notes}</p>
            </div>
          )}

          {shown.source === "status" && shown.expectedBack && shown.result === "oos" && (
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
              <Fact label="Expected back">{formatDate(`${shown.expectedBack}T12:00:00`)}</Fact>
            </dl>
          )}

          {shown.source === "gps" && detail && (
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
              <Fact label="Morning odometer">
                {detail.morning ? formatMiles(detail.morning.odometer) : ""}
                {detail.morning && <span className="app-muted block text-xs font-normal">{formatDateTime(detail.morning.at)}</span>}
              </Fact>
              <Fact label="Night odometer">
                {detail.night ? formatMiles(detail.night.odometer) : ""}
                {detail.night && <span className="app-muted block text-xs font-normal">{formatDateTime(detail.night.at)}</span>}
              </Fact>
              <Fact label="Miles that day">{`${Math.round(detail.miles || 0).toLocaleString()} mi`}</Fact>
            </dl>
          )}

          {shown.type === "vehicle" && shown.source !== "gps" && detail && (
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
              <Fact label="Odometer">{formatMiles(detail.odometer)}</Fact>
              <Fact label="Time taken">
                {formatDuration(detail.durationSeconds)}
                {isQuick(detail) && (
                  <span className="block text-xs font-normal text-amber-700 dark:text-amber-300">Under 30 seconds</span>
                )}
              </Fact>
              <Fact label="In service">{detail.inService ? "Yes" : "No"}</Fact>
              <Fact label="Certified safe">{detail.certifiedSafe ? "Yes" : "No"}</Fact>
              <Fact label="VIN">
                <span className="font-mono text-xs">{detail.registration}</span>
              </Fact>
            </dl>
          )}

          {shown.type === "forklift" && detail && (detail.moffett || detail.branchManager || detail.durationSeconds !== null) && (
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
              {detail.durationSeconds !== null && detail.durationSeconds !== undefined && (
                <Fact label="Time taken">{formatDuration(detail.durationSeconds)}</Fact>
              )}
              {detail.moffett && <Fact label="Type">Moffett</Fact>}
              {detail.branchManager && <Fact label="Branch manager">{detail.branchManager}</Fact>}
            </dl>
          )}

          {shown.source !== "status" && (
          <div className="mt-5">
            <p className="app-eyebrow mb-1.5">Checklist</p>
            {error ? (
              <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
            ) : !detail ? (
              <div className="flex items-center gap-2 py-3 text-sm">
                <Spinner size={16} /> <span className="app-muted">Loading checklist…</span>
              </div>
            ) : detail.checklist.length === 0 ? (
              <p className="app-muted text-sm">
                {detail.source === "gps"
                  ? "Nobody filled in an inspection this day. This entry comes from the truck's GPS odometer, read each morning and night."
                  : detail.type === "vehicle" && !detail.inService
                  ? "The vehicle was reported not in service, so no checklist was filled in."
                  : "No checklist answers were recorded."}
              </p>
            ) : (
              <div className="app-surface max-h-[45vh] overflow-y-auto rounded-xl border">
                {/* -mt-px tucks the first row's divider under the frame
                    (overflow above a scroll box is clipped, not scrollable). */}
                <ul className="-mt-px grid grid-cols-1 sm:grid-cols-2">
                {detail.checklist.map((item) => (
                  <li
                    key={item.key}
                    className="flex items-start gap-2 border-t app-divider px-3 py-2 text-sm"
                  >
                    <span className="mt-px shrink-0">{STATUS_ICON[item.status] || STATUS_ICON.ok}</span>
                    <span className="min-w-0">
                      <span className="block font-medium leading-snug">{item.label}</span>
                      <span className="app-muted block text-xs">
                        {item.value}
                        {item.note ? ` — ${item.note}` : ""}
                      </span>
                    </span>
                  </li>
                ))}
                </ul>
              </div>
            )}
          </div>
          )}

          <div className="mt-6 flex flex-col-reverse gap-2 border-t app-divider pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {detail?.pdfUrl && (
                <a
                  href={detail.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-btn app-btn-sm app-btn-secondary"
                >
                  <PictureAsPdfRoundedIcon sx={{ fontSize: 16 }} /> Original PDF
                </a>
              )}
              {path && (
                <Link to={path} className="app-btn app-btn-sm app-btn-secondary" onClick={onClose}>
                  Open {shown.type === "vehicle" ? "vehicle" : "forklift"}
                </Link>
              )}
            </div>
            <button type="button" className="app-btn app-btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
};
